// Building blocks for the Space page image carousel.
//
// next/image throws when handed an empty/undefined `src`, and because the Space
// page is server-rendered that throw 500s the whole page. Image-less Spaces come
// back from the API with `profile_image: null` (the type says `string`, but the
// runtime value can be null), so the carousel input must be guarded at runtime.
// These pure helpers are the single source of truth for that guard, shared by the
// Space page (input construction) and ImagesCarousel (defensive render).

export type CarouselImage = {
  url: string;
  alt: string;
  // Serve the image directly (bypass the next/image optimizer). Used for the
  // first-slide cover when a pre-sized card derivative exists.
  unoptimized?: boolean;
};

/** Keep only entries whose URL is a non-empty string. */
export function filterValidImages<T extends { url?: string | null }>(
  images: T[] | null | undefined
): T[] {
  return (images ?? []).filter(
    (image) => typeof image?.url === 'string' && image.url.trim() !== ''
  );
}

type SpaceImageInput = {
  name?: string | null;
  profile_image?: string | null;
  profile_image_card?: string | null;
  space_images?: Array<{ id: string | number; image?: string | null }> | null;
};

/**
 * Ordered carousel slides made ONLY of a Space's real images:
 *   C/D — real cover (card derivative preferred, then full image) first, gallery after.
 *   B   — no cover but real gallery → gallery only (first valid gallery image leads).
 *   A   — no cover and no gallery → EMPTY. The caller renders <SpaceImageFallback>
 *         (the abstract Liquid Glass placeholder) instead of the carousel.
 * Gallery entries without a URL are always dropped. No default/photo asset is ever
 * injected here — an image-less Space yields `[]` by design.
 */
export function buildSpaceCarouselImages(space: SpaceImageInput): CarouselImage[] {
  const name = space.name ?? '';
  // Prefer the FULL-resolution profile_image for the cover so the hero renders sharp
  // at any size (Next optimizes it to the right derivative + DPR). The pre-sized card
  // thumbnail (profile_image_card) is a small webp meant for list cards — using it for
  // a large hero looked pixelated. Fall back to the card only when there is no full
  // image, and in that case serve it directly (unoptimized) as before.
  const fullCover = space.profile_image;
  const cardCover = space.profile_image_card;
  const coverUrl = fullCover || cardCover;
  const hasCover = typeof coverUrl === 'string' && coverUrl.trim() !== '';
  const gallery = filterValidImages<CarouselImage>(
    (space.space_images ?? []).map((image) => ({
      url: image.image ?? '',
      alt: `${name}-${image.id}`,
    }))
  );

  // C / D — real cover leads, gallery follows.
  if (hasCover) {
    return [
      {
        url: coverUrl as string,
        alt: `${name}-cover`,
        // Only bypass the optimizer for the pre-rendered card fallback; the full image
        // must go through Next's optimizer to be served sharp at the hero size.
        unoptimized: !fullCover && Boolean(cardCover),
      },
      ...gallery,
    ];
  }

  // B — no cover, but the Space has real gallery images → use them as-is.
  // A — no cover, no gallery → [] (caller shows the Liquid Glass fallback).
  return gallery;
}

/**
 * Mini-galería de la ficha (sección "Galería" bajo el mapa). Devuelve ÚNICAMENTE las
 * imágenes adicionales del registro (space_images / event_images), EXCLUYENDO la portada
 * (profile_image, que ya es el hero y nunca se repite aquí). Reutiliza filterValidImages,
 * así que las entradas sin URL se descartan. `[]` → no se muestra la sección Galería.
 */
export function buildSpaceGalleryImages(space: SpaceImageInput): CarouselImage[] {
  const name = space.name ?? '';
  return filterValidImages<CarouselImage>(
    (space.space_images ?? []).map((image) => ({
      url: image.image ?? '',
      alt: `${name}-${image.id}`,
    }))
  );
}
